import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/authService";
import { Toast } from "../../../components/common/Toast/Toast";

const validatePassword = (password: string): string[] => {
  const errors: string[] = [];
  if (password.length < 8) errors.push("At least 8 characters");
  if (!/[A-Z]/.test(password)) errors.push("At least 1 uppercase letter");
  if (!/[0-9]/.test(password)) errors.push("At least 1 number");
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password))
    errors.push("At least 1 special character");
  return errors;
};

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key") || "";
  const login = searchParams.get("login") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    const pwErrors = validatePassword(password);
    if (pwErrors.length > 0) {
      errors.password = pwErrors.join(", ");
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await authService.resetPassword({ key, login, password });
      setShowToast(true);
      setStatus("sent");
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    }
  };

  if (status === "sent") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
        <Toast
          open={showToast}
          message="Your password has been reset."
          onClose={() => setShowToast(false)}
        />
        <Container maxWidth="sm">
          <Box sx={{ pt: 16, pb: 4, textAlign: "center", fontFamily: 'Nandia' }}>
            <Typography
              variant="h4"
              component="h1"
              align="center"
              sx={{ mb: 2, fontWeight: "normal" }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ mb: 4, color: "text.secondary" }}
            >
              Password reset successful. Please log in with your new password
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/auth")}
              sx={{
                bgcolor: "#000",
                "&:hover": { bgcolor: "#333" },
                textTransform: "none",
                py: 1.5,
                px: 4,
              }}
            >
              Back to Login
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      <Container maxWidth="sm">
        <Box sx={{ pt: 16, pb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{ mb: 3, fontWeight: "normal" }}
          >
            Reset Password
          </Typography>
          <Typography
            variant="body1"
            component="p"
            align="center"
            sx={{ mb: 3, fontWeight: "normal" }}
          >
            Enter your new password below.
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((prev) => ({ ...prev, password: "" }));
            }}
            error={!!fieldErrors.password}
            helperText={fieldErrors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setFieldErrors((prev) => ({ ...prev, confirmPassword: "" }));
            }}
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {status === "error" && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={!password || !confirmPassword || status === "sending"}
            sx={{
              bgcolor: "#000",
              "&:hover": { bgcolor: "#333" },
              textTransform: "none",
              py: 1.5,
            }}
          >
            {status === "sending" ? "Resetting..." : "Confirm"}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
